import React from "react";
import * as SubframeCore from "@subframe/core";
import { Avatar } from "subframe/components/Avatar";
import { Badge } from "subframe/components/Badge";
import { useQuery } from "@tanstack/react-query";
import { fetchEmployee } from "../../../services/retention";
import endpoints from "modules/Retention/constants/endpoints";
import { Tooltip } from "@mui/material";
import { EmployeeData, EmployeeStatus } from "../../../types";
import { Skeleton } from "@mui/material";
import { formatLocalDate } from "utils/helper";
import { formatPhoneNumber } from "utils/phoneNumber";

interface EmployeeHeaderProps {
  employeeId: number;
}

const EmployeeHeaderSkeleton = () => {
  return (
    <div className="flex w-full flex-col lg:flex-row items-start lg:items-end justify-between gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-4 lg:pl-4 lg:pr-6 lg:py-4 shadow-sm flex-wrap overflow-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex flex-col lg:flex-row items-start lg:items-end gap-4 lg:gap-6 w-full min-w-0">
          <div className="flex flex-col items-start gap-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </div>
            <div className="flex flex-col items-start">
              <Skeleton variant="text" width={150} height={20} />
              <Skeleton variant="text" width={180} height={20} />
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-4 w-full lg:w-auto min-w-0 overflow-hidden">
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={100} height={16} />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={80} height={16} />
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={120} height={16} />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={100} height={16} />
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={120} height={16} />
                <Skeleton variant="circular" width={16} height={16} />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton variant="circular" width={16} height={16} />
                <Skeleton variant="text" width={180} height={16} />
                <Skeleton variant="circular" width={16} height={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row md:flex-col items-center md:items-end gap-2 w-full md:w-auto">
        <Skeleton variant="rectangular" width={120} height={36} />
        <Skeleton variant="rectangular" width={80} height={36} />
      </div>
    </div>
  );
};

export const EmployeeHeader: React.FC<EmployeeHeaderProps> = ({
  employeeId
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [endpoints.employee, employeeId],
    queryFn: () => fetchEmployee({ employee_id: employeeId }),
    select: (res) => res?.result?.data,
    enabled: !!employeeId,
  });

  if (isLoading) {
    return <EmployeeHeaderSkeleton />;
  }
  
  if (error) {
    return <div className="p-4 text-red-500">Error loading employee header.</div>;
  }
  
  if (!data) {
    return <EmployeeHeaderSkeleton />;
  }

  const profile = data.profile;
  
  let status = null;
  if (profile.is_at_risk === true) {
    status = EmployeeStatus.AT_RISK;
  } else if (profile.is_at_risk === false || profile.is_at_risk === undefined) {
    if (profile.is_active === true) {
      status = EmployeeStatus.ACTIVE;
    }
  }
  
  const employee: EmployeeData = {
    name: `${profile.first_name} ${profile.last_name}`,
    initials: `${profile.first_name?.[0] ?? ""}${profile.last_name?.[0] ?? ""}`,
    jobTitle: profile.job_title,
    location: profile.office_location,
    state: profile.region,
    status: status,
    hireDate: profile.hired_at,
    tenure: profile.tenure,
    supervisor: profile.supervisor,
    department: profile.category,
    phone: profile.phone_number,
    email: profile.email
  };

  const getBadgeVariant = (status: EmployeeStatus | null) => {
    if (!status) return "neutral";
    switch (status) {
      case EmployeeStatus.AT_RISK:
        return "error";
      case EmployeeStatus.ACTIVE:
        return "success";
      case EmployeeStatus.WARNING:
        return "warning";
      default:
        return "neutral";
    }
  };

  const handleEmail = () => {
    window.open(`mailto:${employee.email}`, "_blank");
  };
  const handlePhone = () => {
    window.open(`tel:${employee.phone}`);
  };
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(employee.email);
      alert("Email copied to clipboard!");
    } catch {
      alert("Failed to copy email");
    }
  };
  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(employee.phone);
      alert("Phone number copied to clipboard!");
    } catch {
      alert("Failed to copy phone number");
    }
  };

  return (
    <div className="flex w-full flex-col 2xl:flex-row items-start 2xl:items-end justify-between gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-4 lg:pl-4 lg:pr-6 lg:py-4 shadow-sm flex-wrap overflow-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
        <Avatar className="h-12 w-12 flex-none" image="">
          {employee.initials}
        </Avatar>
        <div className="flex flex-col 2xl:flex-row items-start 2xl:items-end gap-4 2xl:gap-6 w-full min-w-0">
          <div className="flex flex-col items-start gap-1">
            <div className="flex flex-row items-center gap-2 sm:gap-4 flex-shrink-0">
              <span className="text-header font-header text-neutral-900 whitespace-nowrap">
                {employee.name}
              </span>
              {employee.status && (
                <Badge variant={getBadgeVariant(employee.status)}>
                  {employee.status}
                </Badge>
              )}
            </div>
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-1">
                <span className="text-body-bold font-body-bold text-neutral-500 sm:truncate">
                  {employee.jobTitle}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <div className="flex items-center justify-center gap-1 pb-0.5 cursor-pointer">
                    <span className="text-label-bold font-label-bold text-warning-800 underline sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                      {employee.location}
                    </span>
                </div>
                <span className="text-body-bold font-body-bold text-neutral-500 sm:truncate">
                  , {employee.state}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-4 w-full min-w-0 overflow-hidden">
            <div className="flex flex-col items-start gap-1 min-h-[48px] justify-between">
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherCalendar"
                />
                <span className="text-label font-label text-neutral-500 sm:truncate">
                  {formatLocalDate(employee.hireDate)}
                </span>
              </div>
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherClock"
                />
                <span className="text-label font-label text-neutral-500 sm:truncate">
                  {employee.tenure}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-start gap-1 min-h-[48px] justify-between">
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherUser"
                />
                <span className="text-label font-label text-brand-primary sm:truncate">
                  {employee.supervisor}
                </span>
              </div>
              <div className="flex items-center gap-2 min-h-[20px]">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherFileText"
                />
                <span className="text-label font-label text-neutral-500 sm:truncate">
                  {employee.department}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-start gap-1 min-h-[48px] justify-between">
              <div className="flex items-center gap-2 min-h-[20px] flex-wrap">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherPhone"
                />
                <Tooltip title="Call">
                  <span className="text-label font-label text-neutral-500 underline cursor-pointer sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[180px] break-words" onClick={handlePhone}>
                    {formatPhoneNumber(employee.phone) || employee.phone}
                  </span>
                </Tooltip>
                <Tooltip title="Copy phone number">
                  <span className="cursor-pointer flex-shrink-0" onClick={handleCopyPhone}>
                    <SubframeCore.Icon
                      className="text-body font-body text-neutral-500"
                      name="FeatherCopy"
                    />
                  </span>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2 min-h-[20px] flex-wrap">
                <SubframeCore.Icon
                  className="text-body font-body text-neutral-500 flex-shrink-0"
                  name="FeatherMail"
                />
                <Tooltip title="Send Email">
                  <span className="text-label font-label text-neutral-500 underline cursor-pointer sm:truncate max-w-[120px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px] break-words" onClick={handleEmail}>
                    {employee.email}
                  </span>
                </Tooltip>
                <Tooltip title="Copy email">
                  <span className="cursor-pointer flex-shrink-0" onClick={handleCopyEmail}>
                    <SubframeCore.Icon
                      className="text-body font-body text-neutral-500"
                      name="FeatherCopy"
                    />
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center gap-2 w-full 2xl:w-auto flex-shrink-0">
        {/*<button*/}
        {/*  onClick={handleTeamsMessage}*/}
        {/*  className="flex items-center gap-2 rounded-md border border-solid border-neutral-border px-2 py-2 flex-1 lg:flex-none justify-center lg:justify-start hover:bg-neutral-50 transition-colors"*/}
        {/*>*/}
        {/*  <SubframeCore.Icon*/}
        {/*    className="text-body font-body text-default-font"*/}
        {/*    name="FeatherUsers"*/}
        {/*  />*/}
        {/*  <span className="text-body font-body text-default-font">*/}
        {/*    Teams message*/}
        {/*  </span>*/}
        {/*</button>*/}
        <button
          onClick={handleEmail}
          className="flex items-center gap-2 rounded-md border border-solid border-neutral-border px-2 py-2 w-full 2xl:w-auto justify-center hover:bg-neutral-50 transition-colors flex-shrink-0"
        >
          <SubframeCore.Icon
            className="text-body font-body text-default-font"
            name="FeatherMail"
          />
          <span className="text-body font-body text-default-font">
            Email
          </span>
        </button>
      </div>
    </div>
  );
}; 